import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Users, Brain, TrendingUp, TrendingDown, Flame, Award, Plus, Eye, ShieldCheck, BarChart3, Smartphone, Trophy, Mail, Zap, Send, Activity, Check } from 'lucide-react';
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  ScatterChart, Scatter, LineChart, Line, Legend
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LEVEL_COLORS = { 'Not Started': '#A1A1AA', 'Developing': '#71717a', 'Building': '#52525b', 'Proficient': '#27272a', 'Advanced': '#09090B' };

export default function ParentDashboard() {
  useAuth();
  const [children, setChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState(null);
  const [childProgress, setChildProgress] = useState(null);
  const [insights, setInsights] = useState(null);
  const [digest, setDigest] = useState(null);
  const [bdnf, setBdnf] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLink, setShowLink] = useState(false);
  const [childEmail, setChildEmail] = useState('');
  const [isParent, setIsParent] = useState(false);
  const [screenTimeInput, setScreenTimeInput] = useState('');
  const [screenTimeDate, setScreenTimeDate] = useState(new Date().toISOString().slice(0, 10));
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => { fetchChildren(); }, []);

  const fetchChildren = async () => {
    try {
      const res = await axios.get(`${API}/parent/children`, { withCredentials: true });
      setChildren(res.data.children || []);
      setIsParent(true);
    } catch (err) {
      if (err.response?.status === 401) return;
      setIsParent(false);
    } finally { setLoading(false); }
  };

  const setupParent = async () => {
    try {
      await axios.post(`${API}/parent/setup`, {}, { withCredentials: true });
      setIsParent(true);
      toast.success('Parent account activated');
    } catch { toast.error('Failed'); }
  };

  const linkChild = async () => {
    if (!childEmail.trim()) return;
    try {
      const res = await axios.post(`${API}/parent/link-child`, { email: childEmail }, { withCredentials: true });
      toast.success(`Linked to ${res.data.child_name || 'child'}`);
      setChildEmail('');
      setShowLink(false);
      fetchChildren();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to link'); }
  };

  const viewChild = async (childId) => {
    setSelectedChild(childId);
    setInsights(null);
    setDigest(null);
    setBdnf(null);
    setActiveTab('overview');
    try {
      const [progRes, insRes, digRes, bdnfRes] = await Promise.all([
        axios.get(`${API}/parent/child/${childId}/progress`, { withCredentials: true }),
        axios.get(`${API}/parent/child/${childId}/insights`, { withCredentials: true }),
        axios.get(`${API}/parent/child/${childId}/weekly-digest`, { withCredentials: true }),
        axios.get(`${API}/parent/child/${childId}/bdnf-tracker`, { withCredentials: true }),
      ]);
      setChildProgress(progRes.data);
      setInsights(insRes.data);
      setDigest(digRes.data);
      setBdnf(bdnfRes.data);
    } catch { toast.error('Failed to load child data'); }
  };

  const logScreenTime = async () => {
    if (!screenTimeInput || !selectedChild) return;
    try {
      await axios.post(`${API}/parent/child/${selectedChild}/screen-time`,
        { minutes: parseInt(screenTimeInput), date: screenTimeDate },
        { withCredentials: true });
      toast.success('Screen time logged');
      setScreenTimeInput('');
      // Refresh insights
      const insRes = await axios.get(`${API}/parent/child/${selectedChild}/insights`, { withCredentials: true });
      setInsights(insRes.data);
    } catch { toast.error('Failed to log'); }
  };

  const emailDigest = async () => {
    if (!selectedChild) return;
    try {
      const res = await axios.post(`${API}/parent/child/${selectedChild}/send-digest`, {}, { withCredentials: true });
      if (res.data.status === 'sent') toast.success(`Digest emailed to ${res.data.to}`);
      else toast.error('Failed to send digest');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };


  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><div className="mono-data text-lg animate-pulse-red">Loading...</div></div>;
  }

  if (!isParent) {
    return (
      <div className="min-h-screen" data-testid="parent-setup">
        <div className="border-b border-[#09090B] py-16">
          <div className="container-swiss text-center max-w-lg mx-auto">
            <ShieldCheck className="w-16 h-16 text-[#09090B] mx-auto mb-6" />
            <h1 className="text-3xl md:text-4xl font-black tracking-tighter mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Parent Dashboard</h1>
            <p className="text-zinc-600 mb-4">Monitor your child's cognitive development with real-time BrainMap scores, weekly digests, peer benchmarks, and screen-time correlation.</p>
            <p className="text-sm text-zinc-400 mb-8">Scientifically measured across 5 cognitive dimensions.</p>
            <button onClick={setupParent} className="btn-accent" data-testid="activate-parent-btn">Activate Parent Account</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="parent-dashboard">
      <div className="border-b border-[#09090B] py-8">
        <div className="container-swiss flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <p className="mono-data text-[#09090B] mb-1">Parent Portal</p>
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="parent-heading">
              Child Progress Monitor
            </h1>
          </div>
          <button onClick={() => setShowLink(true)} className="btn-accent text-xs px-4 py-2 flex items-center gap-2 self-start sm:self-auto" data-testid="link-child-btn">
            <Plus className="w-3 h-3" /> Link Child
          </button>
        </div>
      </div>

      {showLink && (
        <div className="border-b border-[#09090B]">
          <div className="container-swiss py-6">
            <div className="flex flex-col sm:flex-row sm:items-end gap-3 sm:gap-4 max-w-md">
              <div className="flex-1">
                <label className="mono-data text-xs text-zinc-500 mb-1 block">Child's Email</label>
                <input value={childEmail} onChange={e => setChildEmail(e.target.value)} placeholder="child@email.com"
                  className="input-swiss text-sm w-full" data-testid="child-email-input" />
              </div>
              <div className="flex gap-2 sm:gap-4">
                <button onClick={linkChild} disabled={!childEmail.trim()} className="btn-primary text-xs px-6 py-3 disabled:opacity-40 flex-1 sm:flex-initial" data-testid="confirm-link-btn">Link</button>
                <button onClick={() => setShowLink(false)} className="btn-secondary text-xs px-4 py-3 flex-1 sm:flex-initial">Cancel</button>
              </div>
            </div>
            <p className="text-xs text-zinc-400 mt-2">Your child must have an account on FOKYES first.</p>
          </div>
        </div>
      )}

      <div className="container-swiss py-8">
        {children.length === 0 ? (
          <div className="border border-[#09090B] p-12 text-center" data-testid="no-children">
            <Users className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>No children linked yet</h3>
            <p className="text-sm text-zinc-500 mb-6">Link your child's account to monitor their cognitive progress.</p>
            <button onClick={() => setShowLink(true)} className="btn-primary text-xs px-6 py-2">Link Child Account</button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Children list */}
            <div className="lg:col-span-1">
              <h3 className="mono-data text-sm mb-4">Your Children</h3>
              <div className="space-y-2">
                {children.map(child => (
                  <button key={child.child_id} onClick={() => viewChild(child.child_id)}
                    className={`w-full text-left border p-4 transition-colors ${selectedChild === child.child_id ? 'border-[#09090B] bg-zinc-100' : 'border-[#09090B] hover:bg-zinc-50'}`}
                    data-testid={`child-card-${child.child_id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>{child.name || child.email}</span>
                      <span className="px-2 py-0.5 text-[10px] font-bold text-white" style={{ background: LEVEL_COLORS[child.level] }}>{child.level}</span>
                    </div>
                    <div className="flex items-center gap-4 mono-data text-xs text-zinc-500">
                      <span className="flex items-center gap-1"><Brain className="w-3 h-3" /> {child.brainmap_score}/900</span>
                      <span className="flex items-center gap-1"><Flame className="w-3 h-3" /> {child.streak}d</span>
                      <span className="flex items-center gap-1"><BarChart3 className="w-3 h-3" /> {child.sessions_completed}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Child detail with tabs */}
            <div className="lg:col-span-2">
              {!childProgress ? (
                <div className="border border-[#09090B] p-12 text-center">
                  <Eye className="w-8 h-8 text-zinc-300 mx-auto mb-4" />
                  <p className="text-zinc-500 text-sm">Select a child to view their progress</p>
                </div>
              ) : (
                <div data-testid="child-progress-detail">
                  {/* Tabs */}
                  <div className="flex border border-[#09090B] mb-6 overflow-x-auto no-scrollbar" data-testid="child-tabs">
                    {[
                      { id: 'overview', label: 'Overview', icon: Brain },
                      { id: 'weekly', label: 'Weekly Digest', icon: Mail },
                      { id: 'bdnf', label: 'BDNF', icon: Activity },
                      { id: 'benchmark', label: 'Peer Benchmark', icon: Trophy },
                      { id: 'screen', label: 'Screen-Time', icon: Smartphone }
                    ].map(t => {
                      const Icon = t.icon;
                      return (
                        <button key={t.id} onClick={() => setActiveTab(t.id)}
                          className={`flex-1 min-w-[72px] px-3 py-2 text-xs font-bold tracking-wide uppercase flex items-center justify-center gap-2 transition-colors whitespace-nowrap ${activeTab === t.id ? 'bg-[#09090B] text-white' : 'bg-white hover:bg-zinc-50'}`}
                          data-testid={`tab-${t.id}`}>
                          <Icon className="w-3 h-3" /> <span className="hidden md:inline">{t.label}</span>
                        </button>
                      );
                    })}
                  </div>

                  {/* Tab: Overview */}
                  {activeTab === 'overview' && (
                    <>
                      <div className="border border-[#09090B] p-4 sm:p-6 mb-6" data-testid="overview-brainmap">
                        <div className="flex items-center justify-between mb-4 gap-2">
                          <h3 className="mono-data text-xs sm:text-sm truncate">BrainMap Score: {childProgress.child_name}</h3>
                          <span className="px-3 py-1 text-white text-xs font-bold whitespace-nowrap" style={{ background: LEVEL_COLORS[childProgress.brainmap.level] }}>
                            {childProgress.brainmap.level}
                          </span>
                        </div>
                        <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-8">
                          <div className="text-center">
                            <div className="text-5xl font-black tracking-tighter text-[#09090B]" style={{ fontFamily: 'JetBrains Mono, monospace' }} data-testid="child-brainmap-score">
                              {childProgress.brainmap.total_score}
                            </div>
                            <div className="mono-data text-xs text-zinc-400 mt-1">/ 900</div>
                          </div>
                          <div className="flex-1 w-full">
                            <ResponsiveContainer width="100%" height={180}>
                              <RadarChart data={Object.entries(childProgress.brainmap.dimensions).map(([k, d]) => ({ subject: d.label.split(' ')[0], score: Math.round(d.score / d.max * 100) }))}>
                                <PolarGrid stroke="#E4E4E7" />
                                <PolarAngleAxis dataKey="subject" tick={{ fontSize: 10 }} />
                                <PolarRadiusAxis angle={90} domain={[0, 100]} tick={false} />
                                <Radar dataKey="score" stroke="#09090B" fill="#09090B" fillOpacity={0.15} strokeWidth={2} />
                              </RadarChart>
                            </ResponsiveContainer>
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-2 sm:gap-4 mt-4 pt-4 border-t border-zinc-200">
                          <div className="text-center">
                            <div className="mono-data text-xs text-zinc-400">Sessions</div>
                            <div className="text-lg font-bold">{childProgress.brainmap.sessions_completed}</div>
                          </div>
                          <div className="text-center">
                            <div className="mono-data text-xs text-zinc-400">Streak</div>
                            <div className="text-lg font-bold flex items-center justify-center gap-1"><Flame className="w-4 h-4 text-[#09090B]" /> {childProgress.brainmap.streak}</div>
                          </div>
                          <div className="text-center">
                            <div className="mono-data text-xs text-zinc-400">Trend</div>
                            <div className={`text-lg font-bold ${childProgress.brainmap.improvement_trend >= 0 ? 'text-[#09090B]' : 'text-[#09090B]'}`}>
                              {childProgress.brainmap.improvement_trend > 0 ? '+' : ''}{childProgress.brainmap.improvement_trend}%
                            </div>
                          </div>
                        </div>
                      </div>

                      {childProgress.programs_progress?.length > 0 && (
                        <div className="border border-[#09090B] p-6 mb-6" data-testid="child-programs">
                          <h3 className="mono-data text-sm mb-4">Program Progress</h3>
                          {childProgress.programs_progress.map(p => (
                            <div key={p.program_id} className="flex items-center gap-4 py-3 border-b border-zinc-200 last:border-0">
                              <div className="flex-1">
                                <span className="text-sm font-medium">{p.title}</span>
                                <div className="h-1.5 bg-zinc-200 mt-2 w-full">
                                  <div className="h-full bg-[#09090B]" style={{ width: `${p.progress_pct}%` }} />
                                </div>
                              </div>
                              <span className="mono-data text-xs text-zinc-500">{p.completed}/{p.total_sessions}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {childProgress.recent_activity?.length > 0 && (
                        <div className="border border-[#09090B] p-6" data-testid="child-activity">
                          <h3 className="mono-data text-sm mb-4">Recent Activity</h3>
                          {childProgress.recent_activity.slice(0, 5).map((a, i) => (
                            <div key={i} className="flex items-center justify-between py-2 border-b border-zinc-100 last:border-0">
                              <span className="text-sm font-mono">{a.session_id}</span>
                              <div className="flex items-center gap-4 mono-data text-xs">
                                <span>Score: {a.score}%</span>
                                <span className="text-zinc-400">{a.date}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}

                  {/* Tab: Weekly Digest */}
                  {activeTab === 'weekly' && digest && (
                    <div data-testid="weekly-digest">
                      <div className="border border-[#09090B] p-4 sm:p-6 mb-6">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                          <div>
                            <h3 className="mono-data text-sm text-[#09090B]">Weekly Digest</h3>
                            <p className="text-lg sm:text-xl font-bold mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Week ending {digest.week_ending}</p>
                          </div>
                          <div className="flex items-center gap-3">
                            <button onClick={emailDigest} className="text-xs mono-data text-[#09090B] hover:underline flex items-center gap-1" data-testid="email-digest-btn">
                              <Send className="w-3 h-3" /> Email me this
                            </button>
                            <Mail className="w-6 h-6 text-[#09090B] hidden sm:block" />
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-2 sm:gap-4">
                          <div className="border border-zinc-200 p-3 sm:p-4">
                            <div className="mono-data text-[10px] sm:text-xs text-zinc-400 mb-1">Sessions</div>
                            <div className="text-xl sm:text-2xl font-black" data-testid="digest-sessions">{digest.this_week.sessions}</div>
                            <div className={`text-[10px] sm:text-xs mt-1 flex items-center gap-1 ${digest.deltas.sessions >= 0 ? 'text-[#09090B]' : 'text-[#09090B]'}`}>
                              {digest.deltas.sessions >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                              {digest.deltas.sessions > 0 ? '+' : ''}{digest.deltas.sessions} vs last wk
                            </div>
                          </div>
                          <div className="border border-zinc-200 p-3 sm:p-4">
                            <div className="mono-data text-[10px] sm:text-xs text-zinc-400 mb-1">Avg Score</div>
                            <div className="text-xl sm:text-2xl font-black" data-testid="digest-avg-score">{digest.this_week.avg_score}%</div>
                            <div className={`text-[10px] sm:text-xs mt-1 flex items-center gap-1 ${digest.deltas.avg_score_pct >= 0 ? 'text-[#09090B]' : 'text-[#09090B]'}`}>
                              {digest.deltas.avg_score_pct >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                              {digest.deltas.avg_score_pct > 0 ? '+' : ''}{digest.deltas.avg_score_pct}%
                            </div>
                          </div>
                          <div className="border border-zinc-200 p-3 sm:p-4">
                            <div className="mono-data text-[10px] sm:text-xs text-zinc-400 mb-1">Training min</div>
                            <div className="text-xl sm:text-2xl font-black" data-testid="digest-minutes">{digest.this_week.minutes}</div>
                            <div className={`text-[10px] sm:text-xs mt-1 flex items-center gap-1 ${digest.deltas.minutes_pct >= 0 ? 'text-[#09090B]' : 'text-[#09090B]'}`}>
                              {digest.deltas.minutes_pct >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                              {digest.deltas.minutes_pct > 0 ? '+' : ''}{digest.deltas.minutes_pct}%
                            </div>
                          </div>
                        </div>

                        {digest.achievements?.length > 0 && (
                          <div className="mt-6">
                            <h4 className="mono-data text-xs text-[#09090B] mb-3">Weekly Wins</h4>
                            <div className="space-y-2" data-testid="digest-achievements">
                              {digest.achievements.map((ach, i) => (
                                <div key={i} className="flex items-center gap-3 bg-zinc-100 border-l-2 border-[#09090B] px-3 py-2">
                                  <Award className="w-4 h-4 text-[#09090B]" />
                                  <span className="text-sm font-medium">{ach.title}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {insights?.weekly_progress && (
                        <div className="border border-[#09090B] p-6" data-testid="weekly-chart">
                          <h3 className="mono-data text-sm text-[#09090B] mb-4">8-Week Progress</h3>
                          <ResponsiveContainer width="100%" height={220}>
                            <BarChart data={insights.weekly_progress}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                              <XAxis dataKey="week" tick={{ fontSize: 10, fontFamily: 'JetBrains Mono' }} />
                              <YAxis tick={{ fontSize: 10 }} />
                              <Tooltip />
                              <Legend wrapperStyle={{ fontSize: 11 }} />
                              <Bar dataKey="avg_score" fill="#09090B" name="Avg Score" />
                              <Bar dataKey="sessions" fill="#09090B" name="Sessions" />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Tab: Peer Benchmark */}
                  {activeTab === 'benchmark' && insights?.peer_benchmark && (
                    <div data-testid="peer-benchmark">
                      <div className="border border-[#09090B] p-4 sm:p-6 mb-6">
                        <div className="flex items-center justify-between mb-6">
                          <div>
                            <h3 className="mono-data text-sm text-[#09090B]">Peer Benchmark</h3>
                            <p className="text-xs text-zinc-500 mt-1">Compared to {insights.peer_benchmark.peer_count} peers on the platform</p>
                          </div>
                          <Trophy className="w-6 h-6 text-[#09090B]" />
                        </div>
                        <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-6">
                          <div className="border border-[#09090B] p-3 sm:p-4 bg-zinc-100">
                            <div className="mono-data text-[10px] sm:text-xs text-[#09090B] mb-1">Your child</div>
                            <div className="text-2xl sm:text-3xl font-black text-[#09090B]" data-testid="benchmark-child-score">{insights.peer_benchmark.child_score}</div>
                            <div className="mono-data text-[10px] sm:text-xs text-zinc-400 mt-1">/ 900</div>
                          </div>
                          <div className="border border-zinc-300 p-3 sm:p-4">
                            <div className="mono-data text-[10px] sm:text-xs text-zinc-400 mb-1">Peer avg</div>
                            <div className="text-2xl sm:text-3xl font-black" data-testid="benchmark-peer-avg">{insights.peer_benchmark.peer_avg}</div>
                            <div className="mono-data text-[10px] sm:text-xs text-zinc-400 mt-1">/ 900</div>
                          </div>
                          <div className="border border-[#09090B] p-3 sm:p-4 bg-zinc-100">
                            <div className="mono-data text-[10px] sm:text-xs text-[#09090B] mb-1">Percentile</div>
                            <div className="text-2xl sm:text-3xl font-black text-[#09090B]" data-testid="benchmark-percentile">{insights.peer_benchmark.percentile}%</div>
                            <div className="mono-data text-[10px] sm:text-xs text-zinc-500 mt-1">ahead of peers</div>
                          </div>
                        </div>

                        <h4 className="mono-data text-xs text-[#09090B] mb-3">Dimension Comparison</h4>
                        <ResponsiveContainer width="100%" height={260}>
                          <BarChart data={insights.peer_benchmark.dimension_comparison} layout="vertical">
                            <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                            <XAxis type="number" domain={[0, 180]} tick={{ fontSize: 10 }} />
                            <YAxis type="category" dataKey="dimension" width={130} tick={{ fontSize: 10 }} />
                            <Tooltip />
                            <Legend wrapperStyle={{ fontSize: 11 }} />
                            <Bar dataKey="child" fill="#09090B" name="Your Child" />
                            <Bar dataKey="peer_avg" fill="#A1A1AA" name="Peer Avg" />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  )}

                  {/* Tab: Screen-Time */}
                  {activeTab === 'screen' && insights?.screen_time && (
                    <div data-testid="screen-time-tab">
                      <div className="border border-[#09090B] p-4 sm:p-6 mb-6">
                        <div className="flex items-center justify-between mb-4">
                          <div>
                            <h3 className="mono-data text-sm text-[#09090B]">Screen-Time Log</h3>
                            <p className="text-xs text-zinc-500 mt-1">Daily minutes on phones/tablets/screens</p>
                          </div>
                          <Smartphone className="w-6 h-6 text-[#09090B]" />
                        </div>

                        <div className="flex flex-col sm:flex-row sm:items-end gap-3 mb-6 pb-6 border-b border-zinc-200">
                          <div>
                            <label className="mono-data text-xs text-zinc-500 mb-1 block">Date</label>
                            <input type="date" value={screenTimeDate} onChange={e => setScreenTimeDate(e.target.value)}
                              className="input-swiss text-sm w-full" data-testid="screen-date-input" />
                          </div>
                          <div className="flex-1">
                            <label className="mono-data text-xs text-zinc-500 mb-1 block">Screen minutes</label>
                            <input type="number" min="0" value={screenTimeInput} onChange={e => setScreenTimeInput(e.target.value)}
                              placeholder="e.g. 180" className="input-swiss text-sm w-full" data-testid="screen-minutes-input" />
                          </div>
                          <button onClick={logScreenTime} className="btn-primary text-xs px-6 py-3 whitespace-nowrap" data-testid="log-screen-btn">Log</button>
                        </div>

                        <div className="grid grid-cols-2 gap-6">
                          <div>
                            <h4 className="mono-data text-xs text-[#09090B] mb-2">Avg daily</h4>
                            <div className="text-4xl font-black">{insights.screen_time.avg_daily_minutes}</div>
                            <div className="mono-data text-xs text-zinc-400">minutes</div>
                          </div>
                          <div>
                            <h4 className="mono-data text-xs text-[#09090B] mb-2">Correlation with scores</h4>
                            <div className={`text-4xl font-black ${insights.screen_time.correlation_coef <= -0.1 ? 'text-[#09090B]' : 'text-zinc-700'}`} data-testid="correlation-coef">
                              {insights.screen_time.correlation_coef}
                            </div>
                            <div className="mono-data text-xs text-zinc-400">-1 to +1</div>
                          </div>
                        </div>

                        <div className="mt-4 p-3 bg-zinc-50 border-l-2 border-[#09090B] flex gap-2 items-start">
                          <Zap className="w-4 h-4 text-[#09090B] flex-shrink-0 mt-0.5" />
                          <p className="text-sm text-zinc-700" data-testid="correlation-interpretation">{insights.screen_time.interpretation}</p>
                        </div>
                      </div>

                      {insights.screen_time.logs.length > 0 && (
                        <div className="border border-[#09090B] p-6 mb-6">
                          <h4 className="mono-data text-xs text-[#09090B] mb-3">Last 14 days</h4>
                          <ResponsiveContainer width="100%" height={200}>
                            <LineChart data={[...insights.screen_time.logs].reverse()}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                              <XAxis dataKey="date" tick={{ fontSize: 9, fontFamily: 'JetBrains Mono' }} />
                              <YAxis tick={{ fontSize: 10 }} label={{ value: 'Minutes', angle: -90, position: 'insideLeft', fontSize: 10 }} />
                              <Tooltip />
                              <Line type="monotone" dataKey="minutes" stroke="#09090B" strokeWidth={2} dot={{ r: 3 }} />
                            </LineChart>
                          </ResponsiveContainer>
                        </div>
                      )}

                      {insights.screen_time.correlation_points.length >= 3 && (
                        <div className="border border-[#09090B] p-4 sm:p-6">
                          <h4 className="mono-data text-xs text-[#09090B] mb-3">Screen time vs. Brain score</h4>
                          <ResponsiveContainer width="100%" height={240}>
                            <ScatterChart>
                              <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                              <XAxis type="number" dataKey="screen_minutes" name="Screen min" tick={{ fontSize: 10 }} label={{ value: 'Screen min', position: 'bottom', fontSize: 10 }} />
                              <YAxis type="number" dataKey="brain_score" domain={[0, 100]} tick={{ fontSize: 10 }} label={{ value: 'Score', angle: -90, position: 'insideLeft', fontSize: 10 }} />
                              <Tooltip cursor={{ strokeDasharray: '3 3' }} />
                              <Scatter data={insights.screen_time.correlation_points} fill="#09090B" />
                            </ScatterChart>
                          </ResponsiveContainer>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Tab: BDNF Weekly Tracker */}
                  {activeTab === 'bdnf' && bdnf && (
                    <div data-testid="bdnf-tracker">
                      <div className="border border-[#09090B] p-4 sm:p-6 mb-6">
                        <div className="flex items-center justify-between mb-4">
                          <div>
                            <h3 className="mono-data text-sm text-[#09090B]">BDNF Weekly Tracker</h3>
                            <p className="text-xs text-zinc-500 mt-1">7 research-informed cognitive habits · past 7 days</p>
                          </div>
                          <Activity className="w-6 h-6 text-[#09090B]" />
                        </div>

                        <div className="grid grid-cols-2 gap-2 sm:gap-4 mb-6 pb-6 border-b border-zinc-200">
                          <div className="border border-zinc-200 p-3 sm:p-4">
                            <div className="mono-data text-[10px] sm:text-xs text-zinc-400 mb-1">Habits touched</div>
                            <div className="text-2xl sm:text-3xl font-black" data-testid="bdnf-habits-touched">
                              {bdnf.summary.habits_touched_this_week}
                              <span className="text-base text-zinc-400 font-normal"> / {bdnf.summary.total_habits}</span>
                            </div>
                            <div className="mono-data text-[10px] sm:text-xs text-zinc-400 mt-1">this week</div>
                          </div>
                          <div className="border border-zinc-200 p-3 sm:p-4">
                            <div className="mono-data text-[10px] sm:text-xs text-zinc-400 mb-1">Sessions completed</div>
                            <div className="text-2xl sm:text-3xl font-black" data-testid="bdnf-sessions-count">
                              {bdnf.summary.total_sessions_this_week}
                            </div>
                            <div className="mono-data text-[10px] sm:text-xs text-zinc-400 mt-1">across BDNF habits</div>
                          </div>
                        </div>

                        {/* Habit × Day grid */}
                        <div className="overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0">
                          <table className="w-full text-xs border-collapse min-w-[520px]" data-testid="bdnf-grid">
                            <thead>
                              <tr>
                                <th className="mono-data text-[10px] text-zinc-400 text-left pb-2 pr-2 font-normal">Habit</th>
                                {bdnf.days.map(d => {
                                  const dt = new Date(d + 'T00:00:00Z');
                                  const dow = dt.toLocaleDateString('en-US', { weekday: 'short', timeZone: 'UTC' }).slice(0, 1);
                                  const day = dt.getUTCDate();
                                  return (
                                    <th key={d} className="mono-data text-[9px] text-zinc-400 text-center pb-2 font-normal">
                                      <div>{dow}</div>
                                      <div className="text-zinc-500 font-semibold">{day}</div>
                                    </th>
                                  );
                                })}
                              </tr>
                            </thead>
                            <tbody>
                              {bdnf.habits.map(h => (
                                <tr key={h.session_id} className="border-t border-zinc-200" data-testid={`bdnf-row-${h.key}`}>
                                  <td className="py-3 pr-2">
                                    <div className="font-semibold text-[11px] sm:text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>{h.label}</div>
                                    <div className="text-[10px] text-zinc-500 hidden sm:block">{h.caption}</div>
                                  </td>
                                  {h.days.map(cell => (
                                    <td key={cell.date} className="text-center py-2 px-0.5">
                                      {cell.practiced ? (
                                        <div className="w-6 h-6 sm:w-8 sm:h-8 bg-[#09090B] text-white rounded-sm flex items-center justify-center mx-auto" title={`Practiced on ${cell.date}`}>
                                          <Check className="w-3 h-3 sm:w-4 sm:h-4" strokeWidth={3} />
                                        </div>
                                      ) : (
                                        <div className="w-6 h-6 sm:w-8 sm:h-8 border border-zinc-200 rounded-sm mx-auto" />
                                      )}
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        <p className="mono-data text-[10px] text-zinc-400 mt-4">
                          Week ending {bdnf.week_ending} · Research-informed, not medical advice.
                        </p>
                      </div>

                      {/* Habit deep-dive list */}
                      <div className="border border-[#09090B] p-4 sm:p-6">
                        <h4 className="mono-data text-xs text-[#09090B] mb-4">Why each habit matters</h4>
                        <div className="space-y-3">
                          {bdnf.habits.map(h => (
                            <div key={h.session_id} className="pb-3 border-b border-zinc-100 last:border-0" data-testid={`bdnf-why-${h.key}`}>
                              <div className="flex items-center justify-between mb-1 gap-2">
                                <span className="font-semibold text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>{h.label}</span>
                                <span className="mono-data text-[10px] text-zinc-400 whitespace-nowrap">
                                  {h.last_practiced ? `Last: ${h.last_practiced}` : 'Not practiced'} · {h.times_30d}× in 30d
                                </span>
                              </div>
                              <p className="text-xs text-zinc-600 leading-relaxed">{h.why}</p>
                            </div>
                          ))}
                        </div>
                        <div className="mt-6 p-3 bg-zinc-50 border-l-2 border-[#09090B]">
                          <p className="text-xs text-zinc-600 leading-relaxed">
                            <strong>Note:</strong> This tracker is an educational tool informed by published research on BDNF and neuroplasticity. It does not diagnose, treat, or predict any medical condition. Consult a qualified health professional for medical concerns.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
